import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatChipsModule } from '@angular/material/chips';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { UserStore } from 'app/features/user/store/user.store';
import { PermissionService } from 'app/core/services/permission.service';
import { NotificationService } from 'app/shared/services/notification.service';
import { CustomersService } from '../../services/customers.service';
import { CustomerBalanceEntryDialogComponent } from '../customer-balance-entry-dialog/customer-balance-entry-dialog.component';
import {
  CustomerLedgerEntry,
  CustomerLedgerSummary,
  formatDateOnly,
  formatLedgerCalendarDate,
  isZeroAmount,
  LEDGER_BUCKET_HINTS,
  LEDGER_BUCKET_LABELS,
  LEDGER_ENTRY_ROLE_LABELS,
  LEDGER_ENTRY_TYPE_LABELS,
  LEDGER_ENTRY_TYPES,
  LEDGER_SOURCE_LABELS,
  LedgerBucket,
  ledgerCorrectionReason,
  ledgerDocumentLabel,
  ledgerEntryRole,
  LedgerEntryRole,
  LedgerEntryType,
  ledgerPosition,
  CustomerLedgerSummary as LedgerSummaryForPosition,
} from '../../models/customer-ledger.model';

/**
 * The Ledger tab on the customer detail view: the customer's statement history
 * from FTE's append-only customer ledger, above the derived position that
 * compares it with what the ERP says is owed.
 *
 * **Read-only with one exception.** The ledger is built by the ERP imports and by
 * Freely's own payments, and a gap between it and the ERP is an observation to
 * investigate, not something to fix from this screen. What a profile running
 * `enableCustomerBalanceEntries` may do here is POST money that has no document —
 * a fee assessed to the account, a credit granted to it — and reverse one it
 * posted. Only its own: an entry from an ERP feed or a Freely payment is never
 * reversible here, because the thing that wrote it is what owns it.
 *
 * A reversal compensates rather than edits, which is the ledger's own rule.
 */
@Component({
  selector: 'app-customer-ledger',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatIconModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatChipsModule,
    MatSlideToggleModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDialogModule,
  ],
  templateUrl: './customer-ledger.component.html',
  host: { class: 'component component-customer-ledger' },
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', opacity: 0 })),
      state('expanded', style({ height: '*', opacity: 1 })),
      transition('expanded <=> collapsed', animate('250ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class CustomerLedgerComponent implements OnInit, OnChanges, OnDestroy {
  private customersService = inject(CustomersService);
  private userStore = inject(UserStore);
  private permissionService = inject(PermissionService);
  private notificationService = inject(NotificationService);
  private dialog = inject(MatDialog);

  @Input() customerId!: string;
  /** Shown in the post-to-balance dialog so the operator can see who they are charging. */
  @Input() customerName: string | null = null;
  /** Open the statement narrowed to one document — set when arriving from an invoice. */
  @Input() documentRef: string | null = null;

  // ── Position ──────────────────────────────────────────────────────────────
  summary: CustomerLedgerSummary | null = null;
  summaryLoading = false;
  summaryFailed = false;

  // ── Statement ─────────────────────────────────────────────────────────────
  rows: CustomerLedgerEntry[] = [];
  total = 0;
  isLoading = false;
  loadError: string | null = null;
  /** Discards a response a newer request has already superseded. */
  private loadSeq = 0;
  private loadSub?: Subscription;
  private summarySub?: Subscription;

  readonly entryTypes = LEDGER_ENTRY_TYPES;
  readonly typeLabels = LEDGER_ENTRY_TYPE_LABELS;
  readonly bucketLabels = LEDGER_BUCKET_LABELS;
  readonly bucketHints = LEDGER_BUCKET_HINTS;
  readonly buckets: LedgerBucket[] = ['verified', 'unverified', 'pending', 'notApplicable'];

  selectedTypes = new Set<LedgerEntryType>();
  bucket: LedgerBucket | '' = '';
  showCorrections = true;
  fromDate = '';
  toDate = '';
  documentSearch = '';
  order: 'asc' | 'desc' = 'desc';
  private searchDebounce?: ReturnType<typeof setTimeout>;

  pageIndex = 0;
  pageSize = 50;
  readonly pageSizeOptions = [25, 50, 100, 250];

  // ── Expanded entry + its correction chain ────────────────────────────────
  expandedLedgerId: string | null = null;
  chainCache: Record<string, CustomerLedgerEntry[]> = {};
  chainLoading = new Set<string>();
  chainFailed = new Set<string>();

  readonly displayedColumns = [
    'expand',
    'date',
    'type',
    'document',
    'reference',
    'amount',
    'runningBalance',
    'bucket',
    'source',
  ];

  ngOnInit(): void {
    if (this.documentRef) {
      this.documentSearch = this.documentRef;
    }
    if (this.customerId) {
      this.loadSummary();
      this.loadEntries();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const customerChanged = changes['customerId'] && !changes['customerId'].firstChange;
    const documentChanged = changes['documentRef'] && !changes['documentRef'].firstChange;
    if (documentChanged) {
      this.documentSearch = this.documentRef ?? '';
    }
    if (customerChanged) {
      this.chainCache = {};
      this.loadSummary();
    }
    if (customerChanged || documentChanged) {
      this.resetAndReload();
    }
  }

  ngOnDestroy(): void {
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.loadSub?.unsubscribe();
    this.summarySub?.unsubscribe();
  }

  // ── Loading ───────────────────────────────────────────────────────────────

  loadSummary(): void {
    this.summaryLoading = true;
    this.summaryFailed = false;
    this.summarySub?.unsubscribe();
    this.summarySub = this.customersService.getCustomerLedgerSummary(this.customerId).subscribe({
      next: (summary) => {
        this.summary = summary;
        this.summaryLoading = false;
      },
      error: () => {
        this.summary = null;
        this.summaryFailed = true;
        this.summaryLoading = false;
      },
    });
  }

  loadEntries(): void {
    if (!this.customerId) return;
    const seq = ++this.loadSeq;
    this.isLoading = true;
    this.loadError = null;
    this.loadSub?.unsubscribe();
    this.loadSub = this.customersService
      .getCustomerLedger(this.customerId, {
        fromDate: this.fromDate || undefined,
        toDate: this.toDate || undefined,
        entryTypes: [...this.selectedTypes],
        bucket: this.bucket || undefined,
        includeReversed: this.showCorrections,
        documentRef: this.documentSearch,
        order: this.order,
        page: this.pageIndex + 1,
        limit: this.pageSize,
      })
      .subscribe({
        next: (page) => {
          if (seq !== this.loadSeq) return;
          this.rows = page.rows;
          this.total = page.count;
          this.isLoading = false;
        },
        error: (err) => {
          if (seq !== this.loadSeq) return;
          // Clear the rows too: leaving the previous page on screen under a new
          // filter reads as that filter's result.
          this.rows = [];
          this.total = 0;
          this.isLoading = false;
          this.loadError = err?.message || 'The ledger could not be loaded.';
        },
      });
  }

  private resetAndReload(): void {
    this.pageIndex = 0;
    this.expandedLedgerId = null;
    this.loadEntries();
  }

  // ── Filters ───────────────────────────────────────────────────────────────

  get isAllTypes(): boolean {
    return this.selectedTypes.size === 0;
  }

  isTypeSelected(type: LedgerEntryType): boolean {
    return this.selectedTypes.has(type);
  }

  toggleType(type: LedgerEntryType): void {
    if (this.selectedTypes.has(type)) this.selectedTypes.delete(type);
    else this.selectedTypes.add(type);
    this.resetAndReload();
  }

  selectAllTypes(): void {
    if (this.selectedTypes.size === 0) return;
    this.selectedTypes.clear();
    this.resetAndReload();
  }

  onBucketChange(): void {
    this.resetAndReload();
  }

  onCorrectionsChange(): void {
    this.resetAndReload();
  }

  onDateChange(): void {
    // A half-typed or inverted range is not a filter yet; FTE would 400 on it.
    if (this.fromDate && this.toDate && this.fromDate > this.toDate) return;
    this.resetAndReload();
  }

  onDocumentSearchChange(): void {
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => this.resetAndReload(), 350);
  }

  clearDocumentSearch(): void {
    this.documentSearch = '';
    this.resetAndReload();
  }

  toggleOrder(): void {
    this.order = this.order === 'desc' ? 'asc' : 'desc';
    this.resetAndReload();
  }

  get hasFilters(): boolean {
    return (
      this.selectedTypes.size > 0 ||
      !!this.bucket ||
      !this.showCorrections ||
      !!this.fromDate ||
      !!this.toDate ||
      this.documentSearch.trim() !== ''
    );
  }

  clearFilters(): void {
    this.selectedTypes.clear();
    this.bucket = '';
    this.showCorrections = true;
    this.fromDate = '';
    this.toDate = '';
    this.documentSearch = '';
    this.resetAndReload();
  }

  onPage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.expandedLedgerId = null;
    this.loadEntries();
  }

  // ── Expansion ─────────────────────────────────────────────────────────────

  isExpanded(entry: CustomerLedgerEntry): boolean {
    return this.expandedLedgerId === entry.ledgerId;
  }

  toggleExpand(entry: CustomerLedgerEntry): void {
    if (this.expandedLedgerId === entry.ledgerId) {
      this.expandedLedgerId = null;
      return;
    }
    this.expandedLedgerId = entry.ledgerId;
    if (this.roleOf(entry) !== 'original') {
      this.loadChain(entry.ledgerId);
    }
  }

  private loadChain(ledgerId: string): void {
    if (this.chainCache[ledgerId] || this.chainLoading.has(ledgerId)) return;
    this.chainLoading.add(ledgerId);
    this.chainFailed.delete(ledgerId);
    this.customersService.getCustomerLedgerEntry(ledgerId).subscribe({
      next: (chain) => {
        this.chainCache[ledgerId] = [...chain.related].sort(
          (a, b) => a.entryDateTime.localeCompare(b.entryDateTime) || a.ledgerId.localeCompare(b.ledgerId)
        );
        this.chainLoading.delete(ledgerId);
      },
      error: () => {
        this.chainFailed.add(ledgerId);
        this.chainLoading.delete(ledgerId);
      },
    });
  }

  // ── Display toggles (profile settings, default on) ───────────────────────

  /**
   * `showLedgerOutOfBalance` / `showLedgerErpVariance`. Read `!== false`: FTE defaults both to
   * true, and an FTE predating them sends nothing, which must keep showing what it always did.
   */
  get showOutOfBalance(): boolean {
    return this.userStore?.activeProfile()?.profileDetail?.profileAdminSettings?.showLedgerOutOfBalance !== false;
  }

  get showErpVariance(): boolean {
    return this.userStore?.activeProfile()?.profileDetail?.profileAdminSettings?.showLedgerErpVariance !== false;
  }

  // ── Posting to the balance (enableCustomerBalanceEntries) ────────────────

  /**
   * May this operator post to a balance at all — the profile's feature flag AND the permission.
   *
   * Both halves, because they answer different questions: the flag says this distributor's
   * accounting works that way, the permission says this person may raise what a customer owes.
   * FTE checks both again (403 either way), so this decides what to show, never what is allowed.
   */
  get canPostBalanceEntry(): boolean {
    const enabled = this.userStore?.activeProfile()?.profileDetail?.profileAdminSettings?.enableCustomerBalanceEntries === true;
    return enabled && this.permissionService.hasPermission('CUSTOMER_BALANCE_ADJUST');
  }

  /** An entry somebody posted by hand, rather than one an ERP feed or a payment wrote. */
  isOperatorEntry(entry: CustomerLedgerEntry): boolean {
    return entry.sourceType === 'Operator';
  }

  /**
   * Only an operator's own entry, still active, and not itself a compensating one.
   *
   * An ERP entry is owned by the feed that wrote it and a Freely payment's entries are derived
   * from the payment, so reversing either here would be undone by the next run or the next sync.
   * FTE refuses all of those too; this keeps the button off rows where it could only fail.
   */
  canReverseEntry(entry: CustomerLedgerEntry): boolean {
    return (
      this.canPostBalanceEntry &&
      this.isOperatorEntry(entry) &&
      !entry.reversedByLedgerId &&
      !entry.reversesLedgerId
    );
  }

  openPostBalanceEntry(): void {
    CustomerBalanceEntryDialogComponent.open(this.dialog, {
      customerId: this.customerId,
      customerName: this.customerName,
    })
      .afterClosed()
      .subscribe((entry) => {
        if (!entry) return;
        this.notificationService.success('Posted to the customer balance.');
        this.reloadAfterWrite();
      });
  }

  openReverseBalanceEntry(entry: CustomerLedgerEntry): void {
    CustomerBalanceEntryDialogComponent.open(this.dialog, {
      customerId: this.customerId,
      customerName: this.customerName,
      reverse: {
        ledgerId: entry.ledgerId,
        entryType: entry.entryType,
        amount: entry.amount,
        reason: (entry.extraFields?.['reason'] as string | undefined) ?? '',
      },
    })
      .afterClosed()
      .subscribe((result) => {
        if (!result) return;
        this.notificationService.success('The entry was reversed.');
        this.reloadAfterWrite();
      });
  }

  /**
   * Both figures move together, so both are re-read: a posted entry changes the statement AND
   * the position above it, and a page showing one updated and the other stale is worse than a
   * page showing neither.
   */
  private reloadAfterWrite(): void {
    this.chainCache = {};
    this.loadSummary();
    this.loadEntries();
  }

  // ── Display helpers ───────────────────────────────────────────────────────

  private get timeZone(): string | undefined {
    return this.userStore?.activeProfile()?.profileDetail?.operatingTimeZone;
  }

  businessDate(value: string | null | undefined): string {
    return formatLedgerCalendarDate(value, this.timeZone);
  }

  dateOnly(value: string | null | undefined): string {
    return formatDateOnly(value);
  }

  roleOf(entry: CustomerLedgerEntry): LedgerEntryRole {
    return ledgerEntryRole(entry);
  }

  roleLabel(entry: CustomerLedgerEntry): string {
    return LEDGER_ENTRY_ROLE_LABELS[this.roleOf(entry)];
  }

  roleHint(entry: CustomerLedgerEntry): string {
    switch (this.roleOf(entry)) {
      case 'reversed':
        return 'Superseded by a correction; its reversal cancels it out';
      case 'compensating':
        return 'Cancels a superseded entry — the ledger is never edited, so a correction is written as a reversal';
      case 'replacement':
        return 'The corrected entry that replaces a reversed one';
      default:
        return '';
    }
  }

  documentLabel(entry: CustomerLedgerEntry): string {
    return ledgerDocumentLabel(entry);
  }

  correctionReason(entry: CustomerLedgerEntry): string | null {
    return ledgerCorrectionReason(entry);
  }

  // Table row context is untyped in the template, so labels are looked up here.
  typeLabel(entry: CustomerLedgerEntry): string {
    return LEDGER_ENTRY_TYPE_LABELS[entry.entryType] ?? entry.entryType;
  }

  bucketLabel(entry: CustomerLedgerEntry): string {
    return LEDGER_BUCKET_LABELS[entry.bucket] ?? entry.bucket;
  }

  bucketHint(entry: CustomerLedgerEntry): string {
    return LEDGER_BUCKET_HINTS[entry.bucket] ?? '';
  }

  sourceLabel(entry: CustomerLedgerEntry): string {
    return LEDGER_SOURCE_LABELS[entry.sourceType] ?? entry.sourceType;
  }

  /** Documents, on account, and what neither explains. */
  position(summary: LedgerSummaryForPosition) {
    return ledgerPosition(summary);
  }

  isZero(value: number | null | undefined): boolean {
    return isZeroAmount(value);
  }

  chainFor(entry: CustomerLedgerEntry): CustomerLedgerEntry[] {
    return this.chainCache[entry.ledgerId] ?? [];
  }

  trackRow = (_: number, entry: CustomerLedgerEntry) => entry.ledgerId;
}
